// Confirmation panel shared by the contact and lead embeds — what replaces the
// form once the message is away.
//
// Deliberately not a hero. The host page already carries the section heading
// and the response promise above the embed, so the panel's whole job is one
// line of news plus where the answer will arrive. The mark is an inline glyph
// on the title's cap-height, not a medallion over a centred stack, and the
// panel sheds the form's padding so the page collapses to the size of the news
// instead of leaving a tall empty card behind.

import { useEffect, useRef } from 'react';
import { Check } from 'lucide-react';

export interface EmbedConfirmationProps {
  theme: string;
  title: string;
  body: string;
}

export function EmbedConfirmation({ theme, title, body }: EmbedConfirmationProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // The panel is much shorter than the form it replaced, so after the reflow
  // the news can sit above the viewport — the user submitted from the bottom of
  // a tall form. Move focus to it, which is also what gets it read: a
  // role="status" region mounted in the same tick as its text is announced
  // inconsistently across screen readers. Scroll only when it is out of view.
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    el.scrollIntoView({
      block: 'nearest',
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }, []);

  return (
    <div className="booking-widget booking-widget--bare" data-bw-theme={theme}>
      <div ref={panelRef} className="embed-card embed-confirm" role="status" tabIndex={-1}>
        <span className="embed-confirm-mark" aria-hidden>
          <Check />
        </span>
        {/* Not a heading: the widget cannot know the host page's outline, and
            the panel is a status message rather than a new section. */}
        <div className="embed-confirm-text">
          <p className="embed-confirm-title">{title}</p>
          <p className="embed-confirm-body">{body}</p>
        </div>
      </div>
    </div>
  );
}
